import { Button, CircularProgress, Stack, Theme, Typography } from '@mui/material';
import {
  CompanyLogoAndName,
  DisplayErrors,
  EntityPlanPanel,
  type TEntityChoice,
} from '@verifime/components';
import { SERVER_ERRORS } from '@verifime/utils';
import type { TConfirmAction, TEntityRequestType, TPlanChosen } from '../entityPlan';
import { useConfirmAction, type TConfirmRefusal } from '../hooks/useConfirmAction';
import { useConfirmEntityPlan, type TConfirmEntityClient } from '../hooks/useConfirmEntityPlan';
import { usePlanAttestation } from '../hooks/usePlanAttestation';
import { useShareInFlight } from '../hooks/useShareInFlight';
import type { TJoinedInvites } from '../joinedInvites';
import {
  DUPLICATE_IDENTITY_MESSAGE,
  HELD_DOCUMENT_TYPE_BACK_TO_HOME_LABEL,
  HELD_DOCUMENT_TYPE_NOT_ACCEPTED_MESSAGE,
} from './confirmEntityCopy';

const HEADING = 'Welcome to VerifiMe';
const SUBHEADING = 'We will be verifying your identity for';

export type ConfirmEntityViewProps = {
  /**
   * The sign-up tracking reference: names the client, and is what the choice is confirmed on.
   * Absent for a journey resumed with no invite in this session: then `client` names the client,
   * and only an already confirmed plan can go on (there is nothing to confirm it with).
   */
  trackingReference?: string;
  /** The client, when there is no tracking reference (read from the customer's requests). */
  client?: TConfirmEntityClient;
  /**
   * The choice to confirm: an entity type, or `individual` for just me. Omit it to show the
   * client's confirmed choice, read from its open requests (nothing is sent again).
   */
  choice?: TEntityChoice;
  /**
   * What the customer chose on the choose screen: a wallet identity (`{ identityId }`) or a new
   * entity (`{}`). Omit it to resume: the row is then the new entity that Continue starts.
   */
  chosen?: TPlanChosen;
  /** The open entity request being worked on, when there are two (the request path). */
  entityRequestType?: TEntityRequestType;
  /**
   * The invites that join the opened one (`useJoinableInvites`): the organisation name the partner
   * declared for the entity chosen names its row.
   */
  joinedInvites?: TJoinedInvites;
  /**
   * Called once the action is done: the choice is confirmed (204 and the requests refetched), or
   * an open request already held it and nothing was sent, and what the plan shares has been shared
   * (a shared identity is granted in either case). It hands on what the plan did and what follows:
   * the wizard to start, the Draft to resume, the end screen after a share, or nothing. The caller
   * decides where that leads. It is not called again by a second click.
   */
  onConfirmed: (action: TConfirmAction) => void;
  /**
   * Called when the confirmation is refused with 409, with the `errorCode` the body carried (none
   * when it had none). No error shows: the caller routes by the code. A 409 with the duplicate
   * identity code or the held document type code does not come here: the view shows its message.
   */
  onConflict: (errorCode?: string) => void;
  /**
   * Called once, just before the first grant of an action is sent. A grant closes the customer's
   * requests, which the caller may decide from, so the caller can hold the screen until it leaves.
   */
  onShareStarted?: () => void;
  /**
   * Called when an action ends without handing on (a failed or refused share, a 409, a failure of
   * the confirmation, or a hand-on that threw): the screen is not being left, so the caller stops
   * holding it. Not called after an action that handed on.
   */
  onShareEnded?: () => void;
  /**
   * Called when the customer asks to change the entity type. The link shows only when this is
   * given and the plan still allows a change.
   */
  onChangeChoice?: () => void;
  /**
   * Hides the change link once a Draft of the requested entity type exists (#616). Off by
   * default; the customer portal always turns it on (#689, no toggle). Off, the link stays unless the
   * plan is on the request path. A granted request never hides it (#688 Q1).
   */
  closeChangeOnEntityDraft?: boolean;
  /**
   * Called when the customer takes the next step after the held document type refusal: back to
   * Home. The button shows only with that refusal and only when this is given. No other refusal or
   * error offers it. Navigation is the caller's.
   */
  onBackToHome?: () => void;
};

/** The declared name goes with the entity the choice names; just me has none. */
const declaredNameFor = (choice: TEntityChoice | undefined, joinedInvites?: TJoinedInvites) =>
  choice && choice !== 'individual'
    ? joinedInvites?.declaredOrganisationNameFor(choice)
    : undefined;

/** One message for the screen's error slot: the refusal wins over the generic server error. */
const errorMessageFor = (
  refusal: TConfirmRefusal | undefined,
  hasServerError: boolean,
): string | undefined => {
  if (refusal === 'duplicate-identity') {
    return DUPLICATE_IDENTITY_MESSAGE;
  }
  if (refusal === 'held-document-type-not-accepted') {
    return HELD_DOCUMENT_TYPE_NOT_ACCEPTED_MESSAGE;
  }
  return hasServerError ? SERVER_ERRORS.default : undefined;
};

/**
 * The confirm screen: the client card and the plan for the choice. Its primary action confirms the
 * choice (`PUT /v1/tracking/{trackingReference}/confirmation`), which creates the requests, unless
 * an open request already holds it (a resumed confirm screen: nothing is sent; a granted request does not count, #688 Q1). Either way it then
 * shares what the plan shares with the client (`POST /v1/permission`, after the confirmation: the
 * grant closes the requests, which must exist first) and hands the plan's action to the caller. A
 * failed share shows the server error and routes nowhere. A person who already holds an Individual
 * identity ticks one attestation before the primary works (618 L2, L8); a brand-new person has none.
 * A step that shares or creates ends with the share footer note. The tick is held in this view, not
 * stored, and starts unticked. ADR 62: the view owns the reads and the calls; navigation is the
 * caller's, via the callbacks.
 */
export default function ConfirmEntityView({
  trackingReference,
  client,
  choice,
  chosen,
  entityRequestType,
  joinedInvites,
  onConfirmed,
  onConflict,
  onShareStarted,
  onShareEnded,
  onChangeChoice,
  closeChangeOnEntityDraft,
  onBackToHome,
}: Readonly<ConfirmEntityViewProps>) {
  const state = useConfirmEntityPlan(trackingReference, choice, client, {
    chosen,
    entityRequestType,
    declaredOrganisationName: declaredNameFor(choice, joinedInvites),
    closeChangeOnEntityDraft,
  });
  const share = useShareInFlight(onShareStarted, onShareEnded);
  const { run, hasError, refusal } = useConfirmAction({
    trackingReference,
    organisationId: state.status === 'loaded' ? state.organisationId : '',
    onConfirmed,
    onConflict,
    onShareStarted: share.onShareStarted,
    onShareEnded: share.onShareEnded,
  });
  const errorMessage = errorMessageFor(refusal, state.status === 'error' || hasError);
  const { attestation, footerNote } = usePlanAttestation(state, share.isSharing);

  return (
    <Stack
      data-testid="confirm-entity-view"
      gap={(theme: Theme) => theme.semantic.spacing.lg}
      sx={(theme: Theme) => ({
        width: '100%',
        maxWidth: theme.semantic.size.sm,
        marginInline: 'auto',
      })}
    >
      <Stack alignItems="center" gap={(theme: Theme) => theme.semantic.spacing.xs}>
        <Typography variant="h4" component="h2" textAlign="center">
          {HEADING}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {SUBHEADING}
        </Typography>
      </Stack>

      {state.status === 'loading' && (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      )}

      {errorMessage && (
        <DisplayErrors
          // The refusal is news the customer acts on, so it is announced; the server error is not,
          // as before.
          role={refusal ? 'alert' : undefined}
          errors={[{ message: errorMessage }]}
        />
      )}

      {refusal === 'held-document-type-not-accepted' && onBackToHome && (
        <Button variant="outlined" onClick={() => onBackToHome()} sx={{ alignSelf: 'center' }}>
          {HELD_DOCUMENT_TYPE_BACK_TO_HOME_LABEL}
        </Button>
      )}

      {state.status === 'loaded' && (
        <>
          <CompanyLogoAndName companyName={state.organisationName} companyLogoUrl={state.logoUrl} />
          <EntityPlanPanel
            rows={state.plan.rows}
            primaryLabel={state.plan.primaryLabel}
            onPrimary={() => void run(state.plan)}
            showChange={state.plan.showChange && !!onChangeChoice}
            onChange={() => onChangeChoice?.()}
            attestation={attestation}
            footerNote={footerNote}
          />
        </>
      )}
    </Stack>
  );
}
